import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Hook pour gérer la confirmation de sortie de page
 * Intercepte :
 * - Fermeture d'onglet/navigateur (beforeunload) - affiche la boîte native du navigateur (limitation navigateur)
 * - Rafraîchissement de la page - affiche la boîte native du navigateur (limitation navigateur)
 * - Bouton retour du navigateur - affiche notre AlertDialog personnalisé
 * - Clics sur les liens internes - affiche notre AlertDialog personnalisé
 * - Navigation manuelle (bouton retour de l'app) - affiche notre AlertDialog personnalisé
 *
 * Note: BrowserRouter ne supporte pas useBlocker, donc on utilise popstate + history.pushState + click interception
 *
 * @param {boolean} shouldBlock - Si la sortie doit être bloquée
 * @param {string} exitPath - Chemin de sortie par défaut
 * @param {Object} options - Options supplémentaires
 * @param {Function} options.onBeforeExit - Callback async appelé avant la sortie (pour sauvegarder)
 */
export function useExitConfirmation(shouldBlock = true, exitPath = null, options = {}) {
    const { onBeforeExit } = options;
    const navigate = useNavigate();
    const location = useLocation();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isBlocking, setIsBlocking] = useState(shouldBlock);
    const [pendingPath, setPendingPath] = useState(null);
    const [exitAction, setExitAction] = useState('navigate'); // 'navigate' | 'reload' | 'back'
    const [isSaving, setIsSaving] = useState(false);
    const exitPathRef = useRef(exitPath);
    const isNavigatingRef = useRef(false);
    const onBeforeExitRef = useRef(onBeforeExit);
    const shouldBlockRef = useRef(shouldBlock);
    shouldBlockRef.current = shouldBlock;

    // Mettre à jour le callback
    useEffect(() => {
        onBeforeExitRef.current = onBeforeExit;
    }, [onBeforeExit]);

    // Mettre à jour le chemin de sortie
    useEffect(() => {
        exitPathRef.current = exitPath;
    }, [exitPath]);

    // Mettre à jour le blocage
    useEffect(() => {
        setIsBlocking(shouldBlock);
    }, [shouldBlock]);

    // Intercepter les clics sur les liens internes
    useEffect(() => {
        if (!isBlocking) return;

        const handleClick = (e) => {
            if (isNavigatingRef.current) return;
            if (!shouldBlockRef.current) return;

            // Trouver le lien le plus proche
            const link = e.target.closest('a[href]');
            if (!link) return;

            const href = link.getAttribute('href');
            if (!href) return;

            // Ignorer les liens externes, les ancres, et les liens avec target
            if (
                href.startsWith('http') ||
                href.startsWith('//') ||
                href.startsWith('#') ||
                href.startsWith('mailto:') ||
                href.startsWith('tel:') ||
                link.getAttribute('target') === '_blank'
            ) {
                return;
            }

            // Ignorer si c'est le même chemin
            if (href === location.pathname) return;

            // Empêcher la navigation
            e.preventDefault();
            e.stopPropagation();

            // Sauvegarder le chemin de destination et ouvrir le dialog
            setPendingPath(href);
            exitPathRef.current = href;
            setExitAction('navigate');
            setIsDialogOpen(true);
        };

        // Capturer les clics en phase de capture pour les intercepter avant React Router
        document.addEventListener('click', handleClick, true);

        return () => {
            document.removeEventListener('click', handleClick, true);
        };
    }, [isBlocking, location.pathname]);

    // Intercepter le bouton retour du navigateur avec popstate
    useEffect(() => {
        if (!isBlocking) return;

        // Ajouter une entrée dans l'historique pour pouvoir intercepter le retour
        window.history.pushState(null, '', window.location.href);

        const handlePopState = (e) => {
            if (isNavigatingRef.current) {
                // On est en train de naviguer volontairement, ne pas bloquer
                return;
            }

            if (!shouldBlockRef.current) return;

            // Empêcher la navigation en rajoutant l'entrée
            window.history.pushState(null, '', window.location.href);

            // Ouvrir notre dialog
            setExitAction('back');
            setIsDialogOpen(true);
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isBlocking, location.pathname]);

    // Intercepter les événements globaux (raccourcis clavier + fermeture onglet)
    useEffect(() => {
        if (!isBlocking) return;

        // Raccourcis clavier de rechargement (F5, Ctrl+R, Cmd+R)
        const handleKeyDown = (e) => {
            if (!shouldBlockRef.current) return;

            // F5
            if (e.key === 'F5') {
                e.preventDefault();
                setExitAction('reload');
                setIsDialogOpen(true);
                return;
            }

            // Ctrl+R (Windows/Linux) ou Cmd+R (Mac)
            if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
                e.preventDefault();
                setExitAction('reload');
                setIsDialogOpen(true);
                return;
            }

            // Ctrl+Shift+R ou Cmd+Shift+R (hard refresh)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'R') {
                e.preventDefault();
                setExitAction('reload');
                setIsDialogOpen(true);
                return;
            }
        };

        // Fermeture de l'onglet/navigateur (affiche la boîte native du navigateur)
        const handleBeforeUnload = (e) => {
            if (!shouldBlockRef.current) return;
            e.preventDefault();
            e.returnValue = 'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?';
            return e.returnValue;
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isBlocking]);

    // Fonction interne pour effectuer la navigation
    const performNavigation = useCallback(() => {
        // Désactiver le blocage AVANT de naviguer pour éviter la boîte native
        setIsBlocking(false);
        setIsDialogOpen(false);
        setIsSaving(false);
        isNavigatingRef.current = true;

        // Attendre le prochain tick pour que les listeners soient retirés
        setTimeout(() => {
            if (exitAction === 'reload') {
                window.location.reload();
            } else if (exitAction === 'back') {
                // Utiliser navigate avec replace pour éviter la boucle d'historique
                // car le pushState a ajouté une entrée supplémentaire
                const targetPath = exitPathRef.current;
                if (targetPath) {
                    navigate(targetPath, { replace: true });
                } else {
                    // Fallback: go(-2) pour sauter l'entrée pushState ET l'entrée actuelle
                    window.history.go(-2);
                }
            } else {
                // Navigation normale
                const targetPath = exitPathRef.current;
                if (targetPath) {
                    navigate(targetPath);
                }
            }
            // Réinitialiser
            setPendingPath(null);
            setExitAction('navigate');
        }, 0);
    }, [navigate, exitAction]);

    // Confirmer la sortie et naviguer (peut appeler onBeforeExit avant)
    const confirmExit = useCallback(async () => {
        // Si un callback onBeforeExit est défini, l'appeler avant de naviguer
        if (onBeforeExitRef.current) {
            setIsSaving(true);
            try {
                await onBeforeExitRef.current();
            } catch (error) {
                console.error('Erreur lors de la sauvegarde avant sortie:', error);
                // On continue quand même la navigation
            }
        }
        performNavigation();
    }, [performNavigation]);

    // Annuler la sortie
    const cancelExit = useCallback(() => {
        setIsDialogOpen(false);
        setPendingPath(null);
        setExitAction('navigate');
        // Restaurer le exitPath par défaut
        exitPathRef.current = exitPath;
    }, [exitPath]);

    // Ouvrir le dialog de confirmation (pour le bouton retour, etc.)
    const requestExit = useCallback(() => {
        setIsDialogOpen(true);
    }, []);

    // Quitter sans sauvegarder (ne pas appeler onBeforeExit)
    const exitWithoutSave = useCallback(() => {
        performNavigation();
    }, [performNavigation]);

    return {
        isDialogOpen,
        setIsDialogOpen: (open) => {
            if (!open) {
                cancelExit();
            } else {
                setIsDialogOpen(true);
            }
        },
        isSaving,
        confirmExit,
        exitWithoutSave,
        cancelExit,
        requestExit,
    };
}
